import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { debounceTime, distinctUntilChanged } from 'rxjs/internal/operators';
import { DpStatsService } from '../../dp-stats.service';
import { DomainDpApproval, DomainDpStatus, DomainDpStatusKL } from '../../../shared/domain/domain-dp-approval.model';
import { CountByDomainDpStatus } from '../../../shared/domain/site-approval-request.model';
import { DpSiteApprovalApproveDialogComponent } from './dp-site-approval-approve-dialog.component';
import { DpSiteApprovalRejectDialogComponent } from './dp-site-approval-reject-dialog.component';
import { HbCsvImportResultDialogComponent } from '../../components/hb-csv-import-result-dialog.component';
import { GlobalSpinnerService } from '../../../shared/components/loader-service/global-spinner.service';
import { SitesCsvImportResult } from '../../domain/sites-csv-import-result.model';
import { SnackBarService } from '../../../shared/services/snackbar.service';
import { CsvExportUtils } from '../../../shared/utils/csv-export-utils';

@Component({
  selector: 'cm-dp-dash-sites',
  templateUrl: './dp-dash-sites.component.html',
  styleUrls: ['./dp-dash-sites.component.scss']
})
export class DpDashSitesComponent implements AfterViewInit, OnInit {
   @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: true}) sort: MatSort;

  selection = new SelectionModel<DomainDpApproval>(true, []);

  DomainDpStatus = DomainDpStatus;
  dataSource = new MatTableDataSource<DomainDpApproval>();

  DomainDpStatusKL = DomainDpStatusKL();

  filter = {
    status: [DomainDpStatus.PENDING],
    domainName: ''
  };
  nameCtrl = new FormControl();

  loading = true;

  count: Map<DomainDpStatus, number>;
  totalCount: number;
  columns = ['select', 'url', 'adstxtStatus', 'status', 'createdOn', 'lastUpdatedOn', 'actions'];

  constructor(private service: DpStatsService,
              private matDialog: MatDialog,
              private globalSpinnerService: GlobalSpinnerService,
              private snackBarService: SnackBarService,
              private dpStatsService: DpStatsService) {
  }

  ngOnInit() {
    this.setDataSourceSorting();
    this.checkIfUserIsRubicon();
    this.refreshDataFirstPage();
  }

  // only rubicon user has columns 'publisher' and 'submitted'
  checkIfUserIsRubicon() {
    this.service.getDemandPartner().subscribe(res => {
      if (res.name.toLocaleLowerCase() === 'rubicon') {
        this.columns = ['select', 'url', 'publisher', 'submitted', 'adstxtStatus', 'status', 'createdOn', 'lastUpdatedOn'];
      }
    });
  }

  setDataSourceSorting() {
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'url':
          return item.id.url; // for nested property only
        default:
          return item[property];
      }
    };
    this.dataSource.sort = this.sort;
  }

  ngAfterViewInit(): void {
    this.paginator.page.subscribe((p: PageEvent) => {
      this.refreshDataPage(p.pageIndex);
    });

    this.nameCtrl.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged())
      .subscribe(next => {
        this.filter.domainName = next;
        this.refreshDataFirstPage();
      });
  }

  refreshDataCurrentPage() {
    this.refreshDataPage(this.paginator.pageIndex);
  }

  refreshDataFirstPage() {
    this.refreshDataPage(0);
  }

  refreshDataPage(page) {
    this.refreshData(page, this.paginator.pageSize, this.filter);
  }

  refreshData(page, pageSize, filter) {
    this.dataSource.data = [];
    this.loading = true;
    this.selection.clear();
    setTimeout(() => {
      this.loading = false;
      this.paginator.length = 10;
      this.service.siteApproval(page, pageSize, filter.status, filter.domainName).subscribe(next => {
        this.paginator.length = next.page.totalElements;
        this.dataSource.data = next.page.content;
        this.count = new Map();
        this.setCount(DomainDpStatus.APPROVED, next.countByStatuses);
        this.setCount(DomainDpStatus.PENDING, next.countByStatuses);
        this.setCount(DomainDpStatus.REJECTED, next.countByStatuses);
        this.totalCount = next.countByStatuses.reduce((a, b) => a + b.count, 0);
      });
    });
  }

  private setCount(s: DomainDpStatus, c: CountByDomainDpStatus[]) {
    const findStatus = c.find(o => o.status === s);
    this.count.set(s, findStatus ? findStatus.count : 0);
  }

  exportCsv() {
  }

  filterByStatus(status: DomainDpStatus) {
    if (!this.isStatusFilterSelected(status)) {
      this.filter.status = this.filter.status.concat([status]);
    } else {
      this.filter.status = this.filter.status.filter(s => s != status);
    }
    this.refreshDataFirstPage();
  }

  isStatusFilterSelected(status: DomainDpStatus) {
    return this.filter.status.includes(status);
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
    // this.selectionChange.emit(this.selection.selected);
  }

  toggle(row) {
    this.selection.toggle(row);
    // this.selectionChange.emit(this.selection.selected);
  }

  approveSites(sites: DomainDpApproval[]) {
    this.matDialog.open(DpSiteApprovalApproveDialogComponent, <MatDialogConfig> {
      data: {
        domains: sites
      }
    }).afterClosed().subscribe(next => {
      if (next) {
        this.refreshDataFirstPage();
      }
    });
  }

  rejectSites(sites: DomainDpApproval[]) {
    this.matDialog.open(DpSiteApprovalRejectDialogComponent, <MatDialogConfig> {
      data: {
        domains: sites
      }
    }).afterClosed().subscribe(next => {
      if (next) {
        this.refreshDataFirstPage();
      }
    });
  }

  uploadFile(file: File) {
    this.globalSpinnerService.showLoader();
    this.dpStatsService.importSites(file).subscribe((next: SitesCsvImportResult) => {
        this.refreshDataCurrentPage();
        this.globalSpinnerService.hideLoader();

        if (next.errorsCount > 0) {
          this.matDialog.open(HbCsvImportResultDialogComponent, <MatDialogConfig>{
            data: {
              result: next
            },
            width: '500px'
          });
        } else {
          this.snackBarService.success('Data imported successfully!');
        }
      },
      () => {
        this.snackBarService.error('Error occured during file upload');
        this.globalSpinnerService.hideLoader();
      });
  }

  exportSites() {
    this.globalSpinnerService.showLoader();
    this.service.exportSites(this.filter.status).subscribe(next => {
      this.globalSpinnerService.hideLoader();
      CsvExportUtils.exportBlob([next.body], 'status-export.csv');
    });
  }
}
